import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import PrintTemplate from '@arcgis/core/rest/support/PrintTemplate';
import PrintParameters from '@arcgis/core/rest/support/PrintParameters';

import { EsriUtils } from '../utils/esri.utils';
import { AppConfigService } from './app-config.service';
import { TranslationService } from './translation.service';
import { availableLanguages } from 'translations';

export enum PaperSizeDimensionType {
  A3 = 'A3',
  A4 = 'A4',
}

export enum LayoutOrientationType {
  Landscape,
  Portrait,
}

export interface AttributesFormPrint {
  titleMap: string;
  author: string;
  typeFile: 'pdf' | 'png32' | 'png8' | 'jpg';
  includesCaption: boolean;
  mode: 'Siia' | 'External' | 'Internal';
  paperSizeDimension: PaperSizeDimensionType;
  layoutOrientation: LayoutOrientationType;
}

type LayoutFormat =
  | 'Layout_Externo_A3'
  | 'Layout_Externo_A4'
  | 'Layout_Externo_SemLegenda_A3'
  | 'Layout_Externo_SemLegenda_A4'
  | 'Layout_Interno_A3'
  | 'Layout_Interno_A4'
  | 'Layout_Interno_SemLegenda_A3'
  | 'Layout_Interno_SemLegenda_A4'
  | 'Layout_SIIA_A3'
  | 'Layout_SIIA_A4'
  | 'Layout_SIIA_SemLegenda_A3'
  | 'Layout_SIIA_SemLegenda_A4';

@Injectable()
export class PrintMapService {
  constructor(
    private appConfigService: AppConfigService,
    private ts: TranslationService
  ) {}

  printMap(
    mapView: __esri.MapView,
    form: AttributesFormPrint
  ): Observable<__esri.PrintResponse> {
    const url = this.appConfigService.normalizeUrl('~printMapService');

    const layout = this.handleTypeLayout(form);

    const titleMap = form.mode === 'Siia' ? '' : form.titleMap;

    const template = new PrintTemplate({
      format: form.typeFile,
      exportOptions: {
        dpi: 300,
      },
      layoutOptions: {
        titleText: titleMap,
        authorText: form.author ?? '',
      },
      scalePreserved: true,
      showLabels: true,
      attributionVisible: true,
      // @ts-ignore
      layout,
    });

    const params = new PrintParameters({
      view: mapView,
      template: template,
    });

    return EsriUtils.printExecute(url, params);
  }

  getLanguageSuffix() {
    if (this.ts.getCurrentLanguageCode() === availableLanguages.en) {
      return '_EN';
    }

    return '';
  }

  openPrintWindow(url: string) {
    window.open(url);
  }

  handleTypeLayout(form: AttributesFormPrint): LayoutFormat {
    const printIncludesCaption = (includeCaption: boolean) => {
      if (!includeCaption) return '_SemLegenda';
      return '';
    };

    let layout = 'Layout';
    switch (form.mode) {
      case 'External':
        layout += '_Externo';
        break;
      case 'Siia':
        layout += '_SIIA';
        break;
      default:
        layout += '_Interno';
        break;
    }

    layout +=
      printIncludesCaption(form.includesCaption) +
      `_${form.paperSizeDimension}`;

    if (form.layoutOrientation === LayoutOrientationType.Landscape) {
      layout += `_Landscape`;
    }

    layout += this.getLanguageSuffix();

    return layout as LayoutFormat;
  }
}
